import React, { useEffect, useState } from 'react';
import { X, Envelope, Check, Warning, MapPin, Copy } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '../../utils/api';

/**
 * MailInCheckModal — rider opt-in flow for paying entries by mailed check.
 *
 * Shows the itemized total (entries + stalls/RV + addons + Office/Rider Fee)
 * WITHOUT the Square service or card processing fees, plus the 14-day
 * disclaimer. Confirming POSTs to /shows/{id}/mail-in-check/confirm which
 * flags every pending entry with awaiting_check=true and a 14-day due date.
 */
export const MailInCheckModal = ({ showId, showName, onClose, onConfirmed }) => {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null); // confirm response payload

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/shows/${showId}/mail-in-check/quote`);
        if (!cancelled) setQuote(data);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || 'Could not load check quote');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [showId]);

  const confirm = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/shows/${showId}/mail-in-check/confirm`);
      const due = data?.check_due_date ? new Date(data.check_due_date) : null;
      const dueLabel = due ? due.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
      toast.success(`Marked for check — please mail by ${dueLabel}`);
      setConfirmed(data);
      onConfirmed?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not mark for mail-in check');
    } finally { setSubmitting(false); }
  };

  const copyAddress = async () => {
    const addr = confirmed?.check_mailing_address || quote?.check_mailing_address;
    if (!addr) return;
    try {
      await navigator.clipboard.writeText(addr);
      toast.success('Address copied to clipboard');
    } catch {
      toast.error('Could not copy — long-press to select manually');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur flex items-center justify-center p-3 sm:p-6 overflow-y-auto" onClick={onClose}>
      <div
        className="bg-[#0A0A0A] border border-[#1F1F1F] rounded-xl w-full max-w-md my-6"
        onClick={(e) => e.stopPropagation()}
        data-testid="mail-in-check-modal"
      >
        <div className="px-5 py-4 border-b border-[#1F1F1F] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Envelope size={18} className="text-[#FF9500]" weight="duotone" />
            <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit' }}>Pay by Mail-in Check</h2>
          </div>
          <button onClick={onClose} className="text-[#9E9E9E] hover:text-white" data-testid="mail-in-check-modal-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-5">
          {confirmed ? (
            <ConfirmedView
              confirmed={confirmed}
              quote={quote}
              showName={showName}
              onCopy={copyAddress}
            />
          ) : (
            <>
              <p className="text-xs text-[#9E9E9E] mb-4">
                {showName ? `For ${showName}.` : ''} Below is the total to mail. No card processing fees are added since you&apos;re paying by check.
              </p>

              {loading ? (
                <div className="py-8 text-center text-[#9E9E9E] text-sm">Calculating total…</div>
              ) : !quote ? (
                <div className="py-8 text-center text-[#FF3B30] text-sm">Could not load quote.</div>
              ) : (
                <>
                  <div className="bg-[#0F0F0F] border border-[#1F1F1F] rounded-lg p-4 mb-4" data-testid="mail-in-check-breakdown">
                    <Row label={`Class Entries (${quote.entries_count})`} amount={quote.entries_total} testid="mail-in-entries" />
                    {quote.stalls_rv_total > 0 && (
                      <Row label="Stalls / RV" amount={quote.stalls_rv_total} testid="mail-in-stalls" />
                    )}
                    {quote.addons_total > 0 && (
                      <Row label="Add-ons" amount={quote.addons_total} testid="mail-in-addons" />
                    )}
                    {quote.office_rider_fee > 0 && (
                      <Row label="Office/Rider Fee" amount={quote.office_rider_fee} testid="mail-in-office-fee" />
                    )}
                    {/* Subtotal — sum BEFORE service fee, so rider can see the math */}
                    {(() => {
                      const subtotal = Number(quote.entries_total || 0)
                        + Number(quote.stalls_rv_total || 0)
                        + Number(quote.addons_total || 0)
                        + Number(quote.office_rider_fee || 0);
                      return (
                        <div className="flex items-center justify-between py-1.5 text-sm border-t border-[#1F1F1F] mt-2" data-testid="mail-in-subtotal">
                          <span className="text-[#9E9E9E] font-semibold">Subtotal</span>
                          <span className="text-white font-mono font-semibold">${subtotal.toFixed(2)}</span>
                        </div>
                      );
                    })()}
                    {quote.service_fee > 0 && (
                      <Row label="Service Fee" amount={quote.service_fee} testid="mail-in-service-fee" />
                    )}
                    {/* Waived line — show the rider what they're saving by paying by check */}
                    <div className="flex items-center justify-between py-1 text-sm" data-testid="mail-in-cc-fee-waived">
                      <span className="text-[#9E9E9E] inline-flex items-center gap-2">
                        C.C. Fee / Payment Processing
                      </span>
                      <span className="inline-flex items-center gap-2">
                        <span className="text-[#9E9E9E] font-mono line-through opacity-60">~3%</span>
                        <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#34C759]/15 text-[#34C759]">Waived</span>
                      </span>
                    </div>
                    <div className="border-t border-[#1F1F1F] mt-3 pt-3 flex items-center justify-between">
                      <span className="text-sm font-bold text-white">Total to Mail</span>
                      <span
                        className="text-3xl font-black text-[#34C759]"
                        style={{ fontFamily: 'Outfit' }}
                        data-testid="mail-in-total-due"
                      >
                        ${(quote.total_due || 0).toFixed(2)}
                      </span>
                    </div>
                  </div>

                  {/* Savings callout — what they'd have paid online vs check */}
                  {(() => {
                    const ccFeeEstimate = Number(quote.total_due || 0) * 0.029 + 0.30;
                    if (ccFeeEstimate < 0.5) return null;
                    const onlineTotal = Number(quote.total_due || 0) + ccFeeEstimate;
                    return (
                      <div className="bg-[#34C759]/10 border border-[#34C759]/30 rounded-lg p-3 mb-3 text-xs flex items-start gap-2" data-testid="mail-in-savings">
                        <Check size={14} weight="bold" className="text-[#34C759] flex-shrink-0 mt-0.5" />
                        <span className="text-[#A0EAB1] leading-relaxed">
                          You save approx <strong className="text-[#34C759]">${ccFeeEstimate.toFixed(2)}</strong> by mailing a check.
                          Online total would have been ~<span className="text-white">${onlineTotal.toFixed(2)}</span>.
                        </span>
                      </div>
                    );
                  })()}

                  <div className="bg-[#FF9500]/10 border border-[#FF9500]/30 rounded-lg p-3 flex items-start gap-2" data-testid="mail-in-disclaimer">
                    <Warning size={16} weight="duotone" className="text-[#FF9500] flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-[#FFB155] leading-relaxed">
                      {quote.disclaimer}
                    </p>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <div className="px-5 py-4 border-t border-[#1F1F1F] flex items-center justify-end gap-2">
          {confirmed ? (
            <button
              onClick={onClose}
              className="px-5 py-2 bg-[#FF9500] hover:bg-[#FFA500] text-black text-sm font-bold rounded inline-flex items-center gap-2"
              data-testid="mail-in-check-done-btn"
            >
              <Check size={14} weight="bold" /> Got it
            </button>
          ) : (
            <>
              <button onClick={onClose} className="px-4 py-2 text-[#9E9E9E] hover:text-white text-sm font-bold">Cancel</button>
              <button
                onClick={confirm}
                disabled={loading || submitting || !quote || (quote?.total_due || 0) <= 0}
                className="px-5 py-2 bg-[#FF9500] hover:bg-[#FFA500] disabled:opacity-60 text-black text-sm font-bold rounded inline-flex items-center gap-2"
                data-testid="mail-in-check-confirm-btn"
              >
                <Check size={14} weight="bold" /> {submitting ? 'Marking…' : "I'll Mail My Check"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, amount, testid }) => (
  <div className="flex items-center justify-between py-1 text-sm" data-testid={testid}>
    <span className="text-[#9E9E9E]">{label}</span>
    <span className="text-white font-mono">${(amount || 0).toFixed(2)}</span>
  </div>
);

/**
 * ConfirmedView — shown after the rider confirms. Displays the producer's
 * mailing address (if set), the total to write the check for, the due date,
 * and a "make checks payable to" line. If the producer didn't configure a
 * mailing address, prompts the rider to contact the producer directly.
 */
const ConfirmedView = ({ confirmed, quote, showName, onCopy }) => {
  const address = confirmed?.check_mailing_address || quote?.check_mailing_address;
  const payable = confirmed?.check_payable_to
    || quote?.check_payable_to
    || confirmed?.producer_contact_name
    || quote?.producer_contact_name;
  const total = confirmed?.total_due ?? quote?.total_due ?? 0;
  const due = confirmed?.check_due_date ? new Date(confirmed.check_due_date) : null;
  const dueLabel = due ? due.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : '';

  return (
    <div data-testid="mail-in-check-confirmed">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-full bg-[#34C759]/15 flex items-center justify-center">
          <Check size={18} weight="bold" className="text-[#34C759]" />
        </div>
        <div>
          <h3 className="text-base font-bold text-white" style={{ fontFamily: 'Outfit' }}>Entries reserved!</h3>
          <p className="text-[11px] text-[#9E9E9E]">Mail your check to the address below.</p>
        </div>
      </div>

      <div className="bg-[#0F0F0F] border border-[#1F1F1F] rounded-lg p-4 mb-3 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs text-[#9E9E9E]">Amount</span>
          <span className="text-2xl font-black text-[#34C759]" style={{ fontFamily: 'Outfit' }} data-testid="mail-in-confirmed-total">
            ${Number(total).toFixed(2)}
          </span>
        </div>
        {payable && (
          <div className="flex items-center justify-between text-xs">
            <span className="text-[#9E9E9E]">Make payable to</span>
            <span className="text-white font-semibold" data-testid="mail-in-payable-to">{payable}</span>
          </div>
        )}
        {dueLabel && (
          <div className="flex items-center justify-between text-xs">
            <span className="text-[#9E9E9E]">Must arrive by</span>
            <span className="text-[#FF9500] font-semibold" data-testid="mail-in-due-date">{dueLabel}</span>
          </div>
        )}
      </div>

      {address ? (
        <div className="bg-[#0F0F0F] border border-[#FF9500]/30 rounded-lg p-4 mb-3" data-testid="mail-in-address-block">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] uppercase tracking-wider text-[#9E9E9E] inline-flex items-center gap-1">
              <MapPin size={12} weight="bold" /> Mailing Address
            </span>
            <button
              onClick={onCopy}
              className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold text-[#FF9500] hover:bg-[#FF9500]/10 rounded transition-colors"
              data-testid="mail-in-copy-address"
            >
              <Copy size={11} weight="bold" /> Copy
            </button>
          </div>
          <pre
            className="whitespace-pre-wrap text-sm text-white leading-relaxed font-sans select-all"
            data-testid="mail-in-address-text"
          >
            {address}
          </pre>
        </div>
      ) : (
        <div className="bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-lg p-3 mb-3 text-xs text-[#FFA8A0]" data-testid="mail-in-no-address">
          <strong>Producer hasn&apos;t set a mailing address yet.</strong> Please contact{' '}
          {payable || 'the show producer'} directly to ask where to send the check.
        </div>
      )}

      <p className="text-[11px] text-[#9E9E9E] leading-relaxed">
        Tip: Write your name and &quot;{showName || 'Show'}&quot; on the memo line so the office can match
        your check to your entries.
      </p>
    </div>
  );
};
